import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, Copy, Check, ArrowRight, Sparkles, Star } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';

const templates = [
  {
    id: 'social-post',
    category: 'Social Media',
    title: 'Viral Social Media Post',
    description: 'Create engaging posts that drive likes, shares, and comments.',
    employee: 'Aria',
    employeeAvatar: '🎀',
    prompt: 'Create a viral [platform] post about [topic]. Include relevant hashtags and a strong call-to-action. The tone should be [casual/professional/humorous].',
    popular: true,
  },
  {
    id: 'customer-response',
    category: 'Customer Support',
    title: 'Customer Complaint Response',
    description: 'Handle complaints with empathy and professionalism.',
    employee: 'Nova',
    employeeAvatar: '💙',
    prompt: 'Write a professional response to this customer complaint: [complaint]. Acknowledge their frustration, apologize sincerely, and provide a solution.',
  },
  {
    id: 'data-analysis',
    category: 'Analytics',
    title: 'Sales Data Analysis',
    description: 'Get insights from your sales numbers.',
    employee: 'Atlas',
    employeeAvatar: '📊',
    prompt: 'Analyze this sales data: [data]. Identify trends, top performers, areas for improvement, and provide actionable recommendations.',
    popular: true,
  },
  {
    id: 'email-sequence',
    category: 'Email Marketing',
    title: 'Welcome Email Sequence',
    description: 'Onboard new subscribers with a compelling email series.',
    employee: 'Echo',
    employeeAvatar: '📧',
    prompt: 'Create a 5-email welcome sequence for new subscribers to [business type]. Include subject lines, body content, and CTAs for each email.',
  },
  {
    id: 'blog-post',
    category: 'Content',
    title: 'SEO Blog Post',
    description: 'Write blog content optimized for search engines.',
    employee: 'Ink',
    employeeAvatar: '✍️',
    prompt: 'Write a 1500-word SEO-optimized blog post about [topic]. Target keyword: [keyword]. Include an engaging intro, subheadings, and a conclusion with CTA.',
    popular: true,
  },
  {
    id: 'sales-pitch',
    category: 'Sales',
    title: 'Cold Email Outreach',
    description: 'Craft compelling cold emails that get responses.',
    employee: 'Spark',
    employeeAvatar: '🎯',
    prompt: 'Write a cold email to [prospect type] introducing [product/service]. Focus on the pain points they face and how we solve them. Keep it under 150 words.',
  },
  {
    id: 'job-description',
    category: 'HR',
    title: 'Job Description',
    description: 'Create compelling job posts that attract talent.',
    employee: 'Scout',
    employeeAvatar: '🔍',
    prompt: 'Write a job description for a [job title] position. Include responsibilities, requirements, benefits, and company culture. Make it engaging and inclusive.',
  },
  {
    id: 'product-description',
    category: 'E-commerce',
    title: 'Product Description',
    description: 'Write descriptions that convert browsers to buyers.',
    employee: 'Flux',
    employeeAvatar: '🛒',
    prompt: 'Write a compelling product description for [product]. Highlight key features, benefits, and include sensory language. Target audience: [audience].',
  },
];

const categories = ['All', 'Social Media', 'Customer Support', 'Email Marketing', 'Content', 'Sales', 'Analytics', 'HR', 'E-commerce'];

export default function Templates() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyPrompt = async (id: string, prompt: string) => {
    await navigator.clipboard.writeText(prompt);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <StaticPageLayout
      title="Prompt Templates"
      subtitle="Ready-to-use prompts to get the best results from your AI employees."
    >
      {/* Search & Filter */}
      <section className="mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedCategory === category
                  ? 'bg-purple-500 text-white'
                  : 'bg-dark-800 border border-white/10 text-gray-300 hover:border-purple-500/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Templates Grid */}
      <section className="mb-16">
        <div className="grid md:grid-cols-2 gap-6">
          {filteredTemplates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-purple-500/30 transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-lg">
                    {template.employeeAvatar}
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">{template.category}</span>
                    <h3 className="text-lg font-semibold text-white">{template.title}</h3>
                  </div>
                </div>
                {template.popular && (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                    <Star className="w-3 h-3" />
                    Popular
                  </span>
                )}
              </div>

              <p className="text-gray-400 text-sm mb-4">{template.description}</p>

              {/* Prompt */}
              <div className="relative p-4 rounded-xl bg-dark-900/50 border border-white/5 mb-4">
                <p className="text-gray-300 text-sm font-mono pr-8">{template.prompt}</p>
                <button
                  onClick={() => copyPrompt(template.id, template.prompt)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
                >
                  {copiedId === template.id ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Works best with <span className="text-purple-400">{template.employee}</span>
                </span>
                <Link
                  to="/login"
                  className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                >
                  Try it <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No templates found matching your criteria.</p>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
        <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-4">Create Your Own Templates</h2>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          Have a prompt that works great? Share it with the community and help others succeed.
        </p>
        <a
          href="mailto:templates@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
        >
          Submit a Template
        </a>
      </section>
    </StaticPageLayout>
  );
}
